import { useState, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { getToFundDataByUser } from "../../redux/invoiceSlice";
import { sanitizeInput, showToast } from "../../utils/utils";
import {
  ASC,
  DUEDATE,
  showToastType
} from "../../utils/constants/appConstants";
import { commonErrMsg } from "../../locales/en";

// Soonest-due first, which is the order the list opens on.
export const DEFAULT_FUND_SORT = { sortBy: DUEDATE, order: ASC };

export const useFundInvoices = () => {
  const dispatch = useDispatch();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const latestRequestIdRef = useRef(0);

  const loadInvoices = useCallback(
    async (page, size, query = searchQuery, sort = DEFAULT_FUND_SORT) => {
      const requestId = ++latestRequestIdRef.current;
      try {
        setLoading(true);
        const payload = {
          query: sanitizeInput(query) || undefined,
          page,
          size,
          sortBy: sort?.sortBy || DEFAULT_FUND_SORT.sortBy,
          order: sort?.order || DEFAULT_FUND_SORT.order
        };

        const res = await dispatch(getToFundDataByUser({ payload })).unwrap();

        // Discard the response if a newer request has since been issued (e.g. the
        // sort changed again before this one returned) so a slow, superseded page
        // can't overwrite the one the headers now claim to show.
        if (requestId !== latestRequestIdRef.current) return;

        setData(res?.data?.data || []);
        setTotalPages(res?.data?.pageInfo?.totalPages || 0);
      } catch (error) {
        if (requestId !== latestRequestIdRef.current) return;

        showToast(
          error?.data?.message || commonErrMsg.item2,
          showToastType.error
        );
      } finally {
        if (requestId === latestRequestIdRef.current) setLoading(false);
      }
    },
    [dispatch, searchQuery]
  );

  return {
    data,
    loading,
    totalPages,
    searchQuery,
    setSearchQuery,
    loadInvoices
  };
};
